import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX } from 'react-icons/fi';
import axiosInstance from '../../api/axios';

// ─── Types ────────────────────────────────────────────────────────────────────

interface QBCategory {
  name: string;
  name_zh?: string;
  display_order: number;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const fetchQBCategories = async (): Promise<QBCategory[]> => {
  const response = await axiosInstance.get('/admin/settings/qb-categories');
  const data = response.data;
  const cats: QBCategory[] = Array.isArray(data.categories) ? data.categories : [];
  return [...cats].sort((a, b) => a.display_order - b.display_order);
};

const saveQBCategories = async (categories: QBCategory[]): Promise<void> => {
  await axiosInstance.put('/admin/settings/qb-categories', { categories });
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const Card = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const AddRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 16px;
  border-bottom: 1px solid #E8DDD0;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

// Wraps a name input with its label, so the EN name field can sit next to a
// paired ZH (Chinese) name field. Mirrors the FormRow/FormGroup pattern used
// by AdminProducts.tsx / AdminCategories.tsx for their EN/ZH input pairs.
const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 4px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #6B5E4E;
`;

const OrderInput = styled.input`
  width: 64px;
  padding: 8px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  background: #FFFFFF;
  color: #1C1511;
  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button<{ variant?: 'danger' | 'success' | 'neutral' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${props => {
    switch (props.variant) {
      case 'danger': return '#fee2e2';
      case 'success': return '#d1fae5';
      case 'neutral': return '#E8DDD0';
      default: return 'rgba(212, 160, 23, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.variant) {
      case 'danger': return '#dc2626';
      case 'success': return '#059669';
      case 'neutral': return '#6B5E4E';
      default: return '#1C1511';
    }
  }};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const CategoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const CategoryItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #E8DDD0;

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryName = styled.span`
  flex: 1;
  font-size: 14px;
  color: #1C1511;
`;

const CategoryNameZh = styled.span`
  flex: 1;
  font-size: 13px;
  color: #6B5E4E;
`;

const OrderBadge = styled.span`
  font-size: 12px;
  color: #6B5E4E;
  min-width: 28px;
  text-align: right;
`;

const EditInput = styled(Input)`
  flex: 1;
  padding: 6px 10px;
`;

const ActionsGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6B5E4E;
  font-size: 14px;
`;

const SaveBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid #E8DDD0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminQBCategories: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: rawCategories, isLoading } = useQuery({
    queryKey: ['adminQBCategories'],
    queryFn: fetchQBCategories,
  });

  const [categories, setCategories] = useState<QBCategory[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryZh, setNewCategoryZh] = useState('');
  const [newDisplayOrder, setNewDisplayOrder] = useState<number>(1);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingValueZh, setEditingValueZh] = useState('');
  const [editingOrder, setEditingOrder] = useState<number>(1);
  const [isDirty, setIsDirty] = useState(false);

  // Sync local state when data loads
  useEffect(() => {
    if (Array.isArray(rawCategories)) {
      setCategories(rawCategories);
      setIsDirty(false);
      const maxOrder = rawCategories.length > 0 ? Math.max(...rawCategories.map(c => c.display_order)) : 0;
      setNewDisplayOrder(maxOrder + 1);
    }
  }, [rawCategories]);

  const saveMutation = useMutation({
    mutationFn: saveQBCategories,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminQBCategories'] });
      setIsDirty(false);
    },
  });

  const handleAdd = () => {
    const trimmed = newCategory.trim();
    if (!trimmed) return;
    const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.display_order)) : 0;
    setCategories(prev => [
      ...prev,
      {
        name: trimmed,
        name_zh: newCategoryZh.trim(),
        display_order: newDisplayOrder > 0 ? newDisplayOrder : maxOrder + 1,
      }
    ]);
    setNewCategory('');
    setNewCategoryZh('');
    setNewDisplayOrder(maxOrder + 2);
    setIsDirty(true);
  };

  const handleAddKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAdd();
  };

  const handleDelete = (index: number) => {
    setCategories(prev => prev.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditingValue(categories[index].name);
    setEditingValueZh(categories[index].name_zh ?? '');
    setEditingOrder(categories[index].display_order);
  };

  const handleConfirmEdit = (index: number) => {
    const trimmed = editingValue.trim();
    if (!trimmed) return;
    setCategories(prev => prev.map((c, i) =>
      i === index
        ? { name: trimmed, name_zh: editingValueZh.trim(), display_order: editingOrder || 1 }
        : c
    ));
    setEditingIndex(null);
    setEditingValue('');
    setEditingValueZh('');
    setEditingOrder(1);
    setIsDirty(true);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingValue('');
    setEditingValueZh('');
    setEditingOrder(1);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Enter') handleConfirmEdit(index);
    if (e.key === 'Escape') handleCancelEdit();
  };

  const handleSave = () => {
    saveMutation.mutate(categories);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  const sortedForDisplay = [...categories]
    .map((c, originalIndex) => ({ ...c, originalIndex }))
    .sort((a, b) => a.display_order - b.display_order);

  return (
    <Container>
      <Header>
        <Title>{t('admin.qbCategories.title')}</Title>
      </Header>

      <Card>
        <AddRow>
          <FormGroup>
            <Label htmlFor="qb-new-category-name">{t('admin.qbCategories.nameLabel')}</Label>
            <Input
              id="qb-new-category-name"
              type="text"
              placeholder={t('admin.qbCategories.namePlaceholder')}
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              onKeyDown={handleAddKeyDown}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="qb-new-category-name-zh">{t('admin.qbCategories.nameZhLabel')}</Label>
            <Input
              id="qb-new-category-name-zh"
              type="text"
              placeholder="新分类名称..."
              value={newCategoryZh}
              onChange={e => setNewCategoryZh(e.target.value)}
              onKeyDown={handleAddKeyDown}
            />
          </FormGroup>
          <OrderInput
            type="number"
            min={1}
            value={newDisplayOrder}
            onChange={e => setNewDisplayOrder(Number(e.target.value))}
            title={t('admin.qbCategories.displayOrderTooltip')}
          />
          <PrimaryButton onClick={handleAdd} disabled={!newCategory.trim()}>
            <FiPlus size={16} />
            {t('admin.qbCategories.addButton')}
          </PrimaryButton>
        </AddRow>

        <CategoryList>
          {categories.length === 0 ? (
            <EmptyState>{t('admin.qbCategories.emptyState')}</EmptyState>
          ) : (
            sortedForDisplay.map(({ originalIndex }) => {
              const category = categories[originalIndex];
              return (
                <CategoryItem key={originalIndex}>
                  {editingIndex === originalIndex ? (
                    <>
                      <FormGroup>
                        <Label htmlFor={`qb-edit-category-name-${originalIndex}`}>{t('admin.qbCategories.nameLabel')}</Label>
                        <EditInput
                          id={`qb-edit-category-name-${originalIndex}`}
                          type="text"
                          value={editingValue}
                          onChange={e => setEditingValue(e.target.value)}
                          onKeyDown={e => handleEditKeyDown(e, originalIndex)}
                          autoFocus
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label htmlFor={`qb-edit-category-name-zh-${originalIndex}`}>{t('admin.qbCategories.nameZhLabel')}</Label>
                        <EditInput
                          id={`qb-edit-category-name-zh-${originalIndex}`}
                          type="text"
                          value={editingValueZh}
                          onChange={e => setEditingValueZh(e.target.value)}
                          onKeyDown={e => handleEditKeyDown(e, originalIndex)}
                        />
                      </FormGroup>
                      <OrderInput
                        type="number"
                        min={1}
                        value={editingOrder}
                        onChange={e => setEditingOrder(Number(e.target.value))}
                        title={t('admin.qbCategories.displayOrderTooltip')}
                      />
                      <ActionsGroup>
                        <IconButton
                          variant="success"
                          onClick={() => handleConfirmEdit(originalIndex)}
                          title={t('admin.qbCategories.saveTooltip')}
                        >
                          <FiSave size={14} />
                        </IconButton>
                        <IconButton
                          variant="neutral"
                          onClick={handleCancelEdit}
                          title={t('admin.qbCategories.cancelTooltip')}
                        >
                          <FiX size={14} />
                        </IconButton>
                      </ActionsGroup>
                    </>
                  ) : (
                    <>
                      <CategoryName>{category.name}</CategoryName>
                      {category.name_zh && <CategoryNameZh>{category.name_zh}</CategoryNameZh>}
                      <OrderBadge>#{category.display_order}</OrderBadge>
                      <ActionsGroup>
                        <IconButton
                          onClick={() => handleStartEdit(originalIndex)}
                          title={t('admin.qbCategories.renameTooltip')}
                        >
                          <FiEdit2 size={14} />
                        </IconButton>
                        <IconButton
                          variant="danger"
                          onClick={() => handleDelete(originalIndex)}
                          title={t('admin.qbCategories.deleteTooltip')}
                        >
                          <FiTrash2 size={14} />
                        </IconButton>
                      </ActionsGroup>
                    </>
                  )}
                </CategoryItem>
              );
            })
          )}
        </CategoryList>

        {isDirty && (
          <SaveBar>
            <PrimaryButton
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              <FiSave size={16} />
              {saveMutation.isPending ? t('admin.qbCategories.savingButton') : t('admin.qbCategories.saveChangesButton')}
            </PrimaryButton>
          </SaveBar>
        )}
      </Card>
    </Container>
  );
};

export default AdminQBCategories;
